import type { ReactNode } from 'react'
import * as stylex from '@stylexjs/stylex'

import Avatar from './Avatar'
import Icon, { type IconName } from './Icon'
import SettleUpLogo from './SettleUpLogo'
import type { User } from '../lib/ledger'
import type { AppSection } from './AppShell'

const styles = stylex.create({
    root: {
        minHeight: '100dvh',
        overflowX: 'hidden',
        backgroundColor: '#f8faf7',
        color: '#27342b',
    },
    header: {
        position: 'sticky',
        top: '0px',
        zIndex: 20,
        display: 'flex',
        minHeight: 'calc(62px + env(safe-area-inset-top))',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottomWidth: 1,
        borderColor: '#e3e9e1',
        backgroundColor: 'color-mix(in srgb, #fffefa 96%, transparent)',
        paddingInline: '16px',
        paddingTop: 'env(safe-area-inset-top)',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 1px 8px rgb(38 59 42 / 3%)',
    },
    logo: {
        height: '30px',
        width: 'auto',
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    profileButton: {
        display: 'flex',
        height: '44px',
        width: '44px',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '9999px',
        WebkitTapHighlightColor: 'transparent',
        ':focus-visible': {
            outline: '2px solid #3f8654',
            outlineOffset: '2px',
        },
        ':active': {
            transform: 'scale(.96)',
        },
    },
    profileRing: {
        display: 'flex',
        height: '38px',
        width: '38px',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '9999px',
        borderWidth: 2,
        borderColor: '#e2ece0',
        backgroundColor: '#fff',
    },
    main: {
        minHeight: 'calc(100dvh - 62px - env(safe-area-inset-top))',
        width: '100%',
        maxWidth: '1024px',
        marginInline: 'auto',
        paddingInline: '16px',
        paddingTop: '18px',
        paddingBottom: 'calc(116px + env(safe-area-inset-bottom))',
        '@media (min-width: 640px)': {
            paddingInline: '28px',
            paddingTop: '28px',
        },
    },
    bottomNav: {
        position: 'fixed',
        insetInline: '0px',
        bottom: '0px',
        zIndex: 30,
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        borderTopWidth: 1,
        borderColor: '#dfe7dd',
        backgroundColor: 'color-mix(in srgb, #fffefa 97%, transparent)',
        paddingInline: '6px',
        paddingBottom: 'max(.45rem, env(safe-area-inset-bottom))',
        paddingTop: '6px',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 -5px 20px rgb(38 59 42 / 5%)',
    },
    navButton: {
        display: 'flex',
        minWidth: 0,
        minHeight: '54px',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        borderRadius: '0.7rem',
        fontSize: '10px',
        fontWeight: 600,
        lineHeight: 1,
        WebkitTapHighlightColor: 'transparent',
        transitionProperty: 'background-color, color, transform',
        transitionDuration: '140ms',
        ':active': {
            transform: 'scale(.96)',
        },
    },
    navSelected: {
        color: '#397d4d',
        backgroundColor: '#edf6eb',
    },
    navIdle: {
        color: '#788178',
    },
    fab: {
        position: 'fixed',
        right: 'max(18px, env(safe-area-inset-right))',
        bottom: 'calc(92px + env(safe-area-inset-bottom))',
        zIndex: 35,
        display: 'flex',
        height: '54px',
        width: '54px',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '9999px',
        borderWidth: 4,
        borderColor: '#f8faf7',
        backgroundColor: '#3f8955',
        color: '#fff',
        boxShadow: '0 7px 20px rgba(43,112,62,.3)',
        WebkitTapHighlightColor: 'transparent',
        transitionProperty: 'background-color, transform, box-shadow',
        transitionDuration: '140ms',
        ':hover': {
            backgroundColor: '#347845',
        },
        ':active': {
            transform: 'scale(.94)',
            boxShadow: '0 4px 12px rgba(43,112,62,.24)',
        },
        ':focus-visible': {
            outline: '2px solid #2d7241',
            outlineOffset: '2px',
        },
    },
})

const navigation: Array<{ section: AppSection; label: string; icon: IconName }> = [
    { section: 'dashboard', label: 'Overview', icon: 'dashboard' },
    { section: 'groups', label: 'Groups', icon: 'groups' },
    { section: 'people', label: 'People', icon: 'people' },
    { section: 'loans', label: 'Loans', icon: 'loans' },
    { section: 'activity', label: 'Activity', icon: 'activity' },
]

type MobileAppShellProps = {
    active: AppSection
    children: ReactNode
    currentUser: User
    onAddExpense: () => void
    onNavigate: (section: AppSection) => void
    onOpenProfile: () => void
}

export default function MobileAppShell({ active, children, currentUser, onAddExpense, onNavigate, onOpenProfile }: MobileAppShellProps) {
    return (
        <div {...stylex.props(styles.root)}>
            <header {...stylex.props(styles.header)}>
                <SettleUpLogo {...stylex.props(styles.logo)} />
                <div {...stylex.props(styles.headerActions)}>
                    <button aria-label="Open your profile" {...stylex.props(styles.profileButton)} onClick={onOpenProfile} type="button">
                        <span {...stylex.props(styles.profileRing)}><Avatar person={currentUser} size={34} /></span>
                    </button>
                </div>
            </header>

            <main {...stylex.props(styles.main)}>{children}</main>

            <nav aria-label="Main navigation" {...stylex.props(styles.bottomNav)}>
                {navigation.map((item) => {
                    const selected = active === item.section
                    return (
                        <button
                            aria-current={selected ? 'page' : undefined}
                            {...stylex.props(styles.navButton, selected ? styles.navSelected : styles.navIdle)}
                            key={item.section}
                            onClick={() => onNavigate(item.section)}
                            type="button"
                        >
                            <Icon name={item.icon} />
                            {item.label}
                        </button>
                    )
                })}
            </nav>

            <button aria-label="Add expense" {...stylex.props(styles.fab)} onClick={onAddExpense} type="button">
                <Icon name="plus" />
            </button>
        </div>
    )
}
